import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { PEOPLE } from './workspaceStore';

// Current "me" for demo purposes. When Supabase is wired, this comes from authStore.
export const CURRENT_USER_ID = 'u_alona';

export interface Update {
  id: string;
  itemId: string;
  authorId: string;
  content: string;       // may contain @u_maya-style mentions (resolved by parser)
  createdAt: string;
  editedAt?: string;
}

export interface Comment {
  id: string;
  updateId: string;
  authorId: string;
  content: string;
  createdAt: string;
}

export interface Reaction {
  id: string;
  updateId?: string;
  commentId?: string;
  emoji: string;
  userId: string;
}

export type NotificationKind =
  | 'mention'        // someone @'d you
  | 'assigned'       // you were assigned to an item
  | 'status_change'  // an item you follow changed status
  | 'due_soon'       // item due soon
  | 'reply'          // someone replied on a thread you started
  | 'invite';        // workspace invite

export interface Notification {
  id: string;
  userId: string;
  kind: NotificationKind;
  title: string;
  body?: string;
  link?: string;           // internal path to the referenced item
  boardId?: string;
  itemId?: string;
  fromUserId?: string;
  isRead: boolean;
  createdAt: string;
}

const uid = (p = 'id') => `${p}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
const now = () => new Date().toISOString();

// ===== Parse @mentions like "@u_maya" from content =====
const MENTION_RE = /@(u_[a-zA-Z0-9_]+)/g;
export function extractMentions(content: string): string[] {
  const out = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = MENTION_RE.exec(content))) {
    if (PEOPLE.some((p) => p.id === m![1])) out.add(m[1]);
  }
  return Array.from(out);
}

export function renderMentionedContent(content: string): Array<{ type: 'text' | 'mention'; value: string }> {
  const parts: Array<{ type: 'text' | 'mention'; value: string }> = [];
  let lastIdx = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(MENTION_RE.source, 'g');
  while ((match = re.exec(content))) {
    const before = content.slice(lastIdx, match.index);
    if (before) parts.push({ type: 'text', value: before });
    const id = match[1];
    const person = PEOPLE.find((p) => p.id === id);
    if (person) parts.push({ type: 'mention', value: person.name });
    else parts.push({ type: 'text', value: match[0] });
    lastIdx = match.index + match[0].length;
  }
  const tail = content.slice(lastIdx);
  if (tail) parts.push({ type: 'text', value: tail });
  return parts;
}

// ===== Seed activity so the demo feels alive =====
function makeSeed() {
  const today = new Date();
  const ago = (mins: number) => {
    const d = new Date(today); d.setMinutes(today.getMinutes() - mins);
    return d.toISOString();
  };

  const updates: Update[] = [
    { id: 'upd_1', itemId: 'itm_seed1', authorId: 'u_maya',  content: 'First mock for the hero is up — @u_alona take a look when you have a sec.', createdAt: ago(120) },
    { id: 'upd_2', itemId: 'itm_seed1', authorId: 'u_alona', content: 'Love this direction. Small note: push the gradient a touch brighter.',         createdAt: ago(60) },
    { id: 'upd_3', itemId: 'itm_seed2', authorId: 'u_noa',   content: 'Blocked on the new brand assets — @u_tal can you unstick me?',                   createdAt: ago(30) },
  ];
  const comments: Comment[] = [
    { id: 'cm_1', updateId: 'upd_1', authorId: 'u_alona', content: 'Gorgeous — ship it after the color tweak.', createdAt: ago(55) },
  ];
  const reactions: Reaction[] = [
    { id: 'rx_1', updateId: 'upd_1', emoji: '', userId: 'u_alona' },
    { id: 'rx_2', updateId: 'upd_1', emoji: '', userId: 'u_noa' },
    { id: 'rx_3', updateId: 'upd_2', emoji: '', userId: 'u_maya' },
  ];
  const notifications: Notification[] = [
    { id: 'nf_1', userId: CURRENT_USER_ID, kind: 'mention',       title: 'Maya mentioned you',       body: 'First mock for the hero is up…',   link: '/boards', fromUserId: 'u_maya', isRead: false, createdAt: ago(120) },
    { id: 'nf_2', userId: CURRENT_USER_ID, kind: 'assigned',      title: 'You were assigned',        body: 'Draft landing page copy',          link: '/boards', fromUserId: 'u_maya', isRead: false, createdAt: ago(200) },
    { id: 'nf_3', userId: CURRENT_USER_ID, kind: 'status_change', title: 'Status moved to Working',  body: 'Real-time cursors',                link: '/boards', fromUserId: 'u_yoni', isRead: true,  createdAt: ago(1440) },
    { id: 'nf_4', userId: CURRENT_USER_ID, kind: 'due_soon',      title: 'Due tomorrow',             body: 'Fix auth redirect bug',            link: '/boards',                        isRead: false, createdAt: ago(240) },
    { id: 'nf_5', userId: CURRENT_USER_ID, kind: 'reply',         title: 'Noa replied to your thread', body: 'Thanks! looks great.',           link: '/boards', fromUserId: 'u_noa',  isRead: true,  createdAt: ago(2880) },
  ];

  return { updates, comments, reactions, notifications };
}

interface ActivityState {
  updates: Update[];
  comments: Comment[];
  reactions: Reaction[];
  notifications: Notification[];

  // Updates
  addUpdate: (itemId: string, content: string, authorId?: string) => string;
  editUpdate: (updateId: string, content: string) => void;
  deleteUpdate: (updateId: string) => void;

  // Comments
  addComment: (updateId: string, content: string, authorId?: string) => string;
  deleteComment: (commentId: string) => void;

  // Reactions
  toggleReaction: (target: { updateId?: string; commentId?: string }, emoji: string, userId?: string) => void;

  // Notifications
  markNotificationRead: (id: string) => void;
  markAllNotificationsRead: () => void;
  clearNotifications: () => void;
  addNotification: (n: Omit<Notification, 'id' | 'createdAt' | 'isRead'>) => void;

  // Selectors (not really mutations, but handy exports)
  resetToSeed: () => void;
}

export const useActivityStore = create<ActivityState>()(
  persist(
    (set, get) => ({
      ...makeSeed(),

      addUpdate: (itemId, content, authorId = CURRENT_USER_ID) => {
        const id = uid('upd');
        const update: Update = { id, itemId, authorId, content, createdAt: now() };
        set((s) => ({ updates: [update, ...s.updates] }));

        // Fan out @mention notifications
        for (const mentionedId of extractMentions(content)) {
          if (mentionedId === authorId) continue;
          const author = PEOPLE.find((p) => p.id === authorId);
          get().addNotification({
            userId: mentionedId,
            kind: 'mention',
            title: `${author?.name ?? 'Someone'} mentioned you`,
            body: content.slice(0, 120),
            itemId,
            fromUserId: authorId,
          });
        }
        return id;
      },
      editUpdate: (updateId, content) => set((s) => ({
        updates: s.updates.map((u) => u.id === updateId ? { ...u, content, editedAt: now() } : u),
      })),
      deleteUpdate: (updateId) => set((s) => ({
        updates: s.updates.filter((u) => u.id !== updateId),
        comments: s.comments.filter((c) => c.updateId !== updateId),
        reactions: s.reactions.filter((r) => r.updateId !== updateId),
      })),

      addComment: (updateId, content, authorId = CURRENT_USER_ID) => {
        const id = uid('cm');
        set((s) => ({
          comments: [...s.comments, { id, updateId, authorId, content, createdAt: now() }],
        }));

        // Notify the update author when someone else replies
        const parent = get().updates.find((u) => u.id === updateId);
        if (parent && parent.authorId !== authorId) {
          const author = PEOPLE.find((p) => p.id === authorId);
          get().addNotification({
            userId: parent.authorId,
            kind: 'reply',
            title: `${author?.name ?? 'Someone'} replied to your update`,
            body: content.slice(0, 120),
            itemId: parent.itemId,
            fromUserId: authorId,
          });
        }

        // Notify @mentions too
        for (const mentionedId of extractMentions(content)) {
          if (mentionedId === authorId) continue;
          const author = PEOPLE.find((p) => p.id === authorId);
          get().addNotification({
            userId: mentionedId,
            kind: 'mention',
            title: `${author?.name ?? 'Someone'} mentioned you`,
            body: content.slice(0, 120),
            itemId: parent?.itemId,
            fromUserId: authorId,
          });
        }
        return id;
      },
      deleteComment: (commentId) => set((s) => ({
        comments: s.comments.filter((c) => c.id !== commentId),
        reactions: s.reactions.filter((r) => r.commentId !== commentId),
      })),

      toggleReaction: (target, emoji, userId = CURRENT_USER_ID) => set((s) => {
        const existing = s.reactions.find((r) =>
          r.userId === userId && r.emoji === emoji &&
          r.updateId === target.updateId && r.commentId === target.commentId,
        );
        if (existing) {
          return { reactions: s.reactions.filter((r) => r.id !== existing.id) };
        }
        return {
          reactions: [...s.reactions, {
            id: uid('rx'),
            updateId: target.updateId,
            commentId: target.commentId,
            emoji, userId,
          }],
        };
      }),

      markNotificationRead: (id) => set((s) => ({
        notifications: s.notifications.map((n) => n.id === id ? { ...n, isRead: true } : n),
      })),
      markAllNotificationsRead: () => set((s) => ({
        notifications: s.notifications.map((n) => ({ ...n, isRead: true })),
      })),
      clearNotifications: () => set({ notifications: [] }),
      addNotification: (n) => set((s) => ({
        notifications: [{ ...n, id: uid('nf'), createdAt: now(), isRead: false }, ...s.notifications],
      })),

      resetToSeed: () => set(makeSeed()),
    }),
    { name: 'myday-activity', version: 1 },
  ),
);

// ===== Selectors (memoized by source — see workspaceStore note) =====
function memoSelector<T>(compute: (s: ActivityState) => T): (s: ActivityState) => T {
  let cache: { s: ActivityState; out: T } | null = null;
  return (s) => {
    if (cache && cache.s === s) return cache.out;
    const out = compute(s);
    cache = { s, out };
    return out;
  };
}

const _updatesByItem   = new Map<string, (s: ActivityState) => Update[]>();
const _commentsByUpdate= new Map<string, (s: ActivityState) => Comment[]>();
const _reactionsByUpdate = new Map<string, (s: ActivityState) => Reaction[]>();

export const selectUpdatesForItem = (itemId: string) => {
  let fn = _updatesByItem.get(itemId);
  if (!fn) {
    fn = memoSelector((s) => s.updates.filter((u) => u.itemId === itemId).sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    _updatesByItem.set(itemId, fn);
  }
  return fn;
};

export const selectCommentsForUpdate = (updateId: string) => {
  let fn = _commentsByUpdate.get(updateId);
  if (!fn) {
    fn = memoSelector((s) => s.comments.filter((c) => c.updateId === updateId).sort((a, b) => a.createdAt.localeCompare(b.createdAt)));
    _commentsByUpdate.set(updateId, fn);
  }
  return fn;
};

export const selectReactionsForUpdate = (updateId: string) => {
  let fn = _reactionsByUpdate.get(updateId);
  if (!fn) {
    fn = memoSelector((s) => s.reactions.filter((r) => r.updateId === updateId));
    _reactionsByUpdate.set(updateId, fn);
  }
  return fn;
};

export const selectMyNotifications = memoSelector((s: ActivityState) =>
  s.notifications
    .filter((n) => n.userId === CURRENT_USER_ID)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
);

export const selectUnreadCount = (s: ActivityState): number =>
  s.notifications.reduce((acc, n) => acc + (n.userId === CURRENT_USER_ID && !n.isRead ? 1 : 0), 0);
